import { useQuery } from "@tanstack/react-query";
import AlertComponent from "components/alert";
import { useAuth } from "contexts/auth";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { IPerson } from "./types";
import { GraphLifes } from "./GraphLifes";
import mockData from "./mockSeller.json";
import { Grid, FormControl, InputLabel, Select, MenuItem, Skeleton, Box } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Day } from 'components/calendar-week-day';
import { Dayjs, dayjs } from "utils/custom-date";
import api from "services/api";

export function ProductivityTabSeller() {
  const history = useHistory();

  const { user } = useAuth();
  const isSellerWithoutTheme = !user?.theme;
  const organizationCnpj = user?.organization?.cnpj;

  // Estados para o range de datas (usando Dayjs para compatibilidade com DatePicker)
  // Padrão: últimos 30 dias (data final = hoje, data inicial = 30 dias atrás)
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(30, 'days'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  
  // Estado para filtro de organização
  const [selectedOrganization, setSelectedOrganization] = useState<string>("Todos");

  function handleToNavigateToCreateCustomThema() {
    history.push('/themes/configure');
  };

  // Manipulador de mudança de data (similar ao form-filter)
  const handleChangeDate = (date: Dayjs | null) => {
    return date;
  };

  // Effect para validar e sincronizar as datas (baseado no form-filter)
  useEffect(() => {
    if (startDate && endDate) {
      // Se a data final for anterior à inicial, ajusta a data final
      if (endDate.isBefore(startDate)) {
        setEndDate(startDate);
      }
      
      // Se a data inicial for posterior à final, ajusta a data inicial  
      if (startDate.isAfter(endDate)) {
        setStartDate(endDate);
      }
    }
  }, [startDate, endDate]);

  // Determinar as datas a serem passadas para o GraphLifes (formato YYYY-MM-DD)
  const graphStartDate = startDate ? startDate.format('YYYY-MM-DD') : undefined;
  const graphEndDate = endDate ? endDate.format('YYYY-MM-DD') : undefined;

  // Função para extrair organizações únicas dos dados
  const getUniqueOrganizations = (data: IPerson[]): string[] => {
    if (!data || data.length === 0) return [];
    
    const organizations = data.map(person => person.organizationName);
    const uniqueOrganizations = Array.from(new Set(organizations));
    return uniqueOrganizations.sort();
  };

  // Função para filtrar dados por organização
  const getFilteredData = (data: IPerson[]): IPerson[] => {
    if (!data || selectedOrganization === "Todos") {
      return data;
    }
    
    return data.filter(person => person.organizationName === selectedOrganization);
  };

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["collaboratorsTables", "seller", graphStartDate, graphEndDate],
    queryFn: () => api.get<{ data: IPerson[] }>(`/v2/cdp/summary-productivity/${organizationCnpj}?startDate=${graphStartDate}&endDate=${graphEndDate}`),
    select: (data) => data.data.data,
    enabled: !!graphStartDate && !!graphEndDate,
  });

  // Calcular organizações únicas e dados filtrados
  const uniqueOrganizations = data ? getUniqueOrganizations(data) : [];
  const filteredData = data ? getFilteredData(data) : [];

  if (isFetching) {
    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={2}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={2}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={2}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Grid>
        </Grid>
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, p: 4, boxShadow: 1 }}>
          <Skeleton variant="text" width={200} height={36} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={300} height={20} sx={{ mb: 4 }} />
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            </Grid>
          </Grid>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1, mt: 6 }} />
        </Box>
      </Box>
    )
  }

  return (
    <>
      {isSellerWithoutTheme && (
        <AlertComponent
          severity='info'
          title='Temos uma novidade para você!'
          content='Agora você pode personalizar sua marca para que seu cliente veja a sua identidade visual.'
          navigateText='Clique aqui para personalizar'
          onNavigate={handleToNavigateToCreateCustomThema}
        />
      )}

      {/* Filtros - seguindo padrão do form-filter */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={2}>
          <DatePicker
            label='Data Inicial'
            format='DD/MM/YYYY'
            disableHighlightToday
            showDaysOutsideCurrentMonth
            displayWeekNumber
            maxDate={dayjs(new Date())}
            value={startDate}
            onChange={(date) => setStartDate(handleChangeDate(date))}
            slots={{ day: Day }}
            slotProps={{
              textField: {
                size: 'small',
                margin: 'none',
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <DatePicker
            label='Data Final'
            format='DD/MM/YYYY'
            disableHighlightToday
            maxDate={dayjs(new Date())}
            minDate={startDate || undefined}
            showDaysOutsideCurrentMonth
            displayWeekNumber
            value={endDate}
            onChange={(date) => setEndDate(handleChangeDate(date))}
            slots={{ day: Day }}
            slotProps={{
              textField: {
                size: 'small',
                margin: 'none',
                fullWidth: true,
                variant: 'outlined',
              },
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <FormControl
            fullWidth
            size="small"
          >
            <InputLabel id="organization-select-label">Organização</InputLabel>
            <Select
              labelId="organization-select-label"
              label="Organização"
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              {uniqueOrganizations.map((organization) => (
                <MenuItem key={organization} value={organization}>
                  {organization}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {filteredData && (
        <GraphLifes
          data={filteredData}
          startDate={graphStartDate}
          endDate={graphEndDate}
        />
      )}
    </>
  )
}
